import type { List, ListItem } from 'mdast';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { State } from '../state';
import { isMalformedTaskItem } from '../utils/malformed-task-utils';

/** Returns true if a list item should be treated as a task item (gate ON assumed). */
function isTaskItem(item: ListItem): boolean {
	return (item.checked !== null && item.checked !== undefined) || isMalformedTaskItem(item);
}

export function list(state: State, node: List): void {
	// If the schema has a taskList node and ALL items are task items (checked !== null/undefined),
	// use taskList instead of bullet_list.
	const taskListType = state.schema.nodes.taskList ?? state.schema.nodes.task_list;

	// Fast path: all items are task items → emit a single taskList.
	if (
		!node.ordered &&
		taskListType &&
		node.children.length > 0 &&
		node.children.every(
			(item: ListItem) =>
				(item.checked !== null && item.checked !== undefined) ||
				(isMalformedTaskItem(item) && fg('platform_editor_markdown_patch_m3')),
		)
	) {
		state.openNode(taskListType);
		state.parseTokens(node.children);
		state.closeNode();
		return;
	}

	// Split path: an unordered list that contains task items AND non-task
	// items at the boundaries (e.g. `* []` with no space typed before/after the group).
	// Non-task items at the edges should NOT pull the whole list into a bullet_list;
	// instead we emit consecutive task items as a taskList segment and non-task items
	// as individual bullet_list segments.
	if (!node.ordered && taskListType && fg('platform_editor_markdown_patch_m3')) {
		const hasAnyTask = node.children.some((item: ListItem) => isTaskItem(item));
		if (hasAnyTask) {
			emitSegments(state, node, taskListType);
			return;
		}
	}

	const nodeType = node.ordered
		? (state.schema.nodes.ordered_list ?? state.schema.nodes.orderedList)
		: (state.schema.nodes.bullet_list ?? state.schema.nodes.bulletList);

	if (!nodeType) {
		// Gracefully degrade: schema has no list node — render children directly as block content
		state.parseTokens(node.children);
		return;
	}

	const attrs = node.ordered ? { order: node.start ?? 1 } : null;
	state.openNode(nodeType, attrs);
	state.parseTokens(node.children);
	state.closeNode();
}

/**
 * Emits a mixed unordered list as alternating taskList / bullet_list segments.
 * Consecutive task items → taskList, consecutive non-task items → bullet_list.
 */
function emitSegments(
	state: State,
	node: List,
	taskListType: Parameters<State['openNode']>[0],
): void {
	// Single pass: open a taskList for consecutive task items, emit non-task items
	// as individual bullet_list nodes.
	let inTaskList = false;

	node.children.forEach((item) => {
		if (isTaskItem(item)) {
			if (!inTaskList) {
				state.openNode(taskListType);
				inTaskList = true;
			}
			state.parseTokens([item]);
		} else {
			if (inTaskList) {
				state.closeNode();
				inTaskList = false;
			}
			// Non-task item: treat as whatever it is — a plain unordered list item.
			state.openNode(state.schema.nodes.bullet_list ?? state.schema.nodes.bulletList);
			state.parseTokens([item]);
			state.closeNode();
		}
	});

	if (inTaskList) {
		state.closeNode();
	}
}
